import { useEffect, useState, useMemo } from "react";
import { Box, Heading, HStack, VStack, SimpleGrid, FormControl, FormErrorMessage, FormLabel, Input, InputGroup, InputLeftElement, Select, Textarea, Checkbox, Radio, RadioGroup, Button, Text, Flex, Icon, IconProps } from "@chakra-ui/react";
import { CustomToast } from "../../components";
import { useFetch } from "../../hooks/useFetch";
import { cardProps } from "./billingui";

type Props = { customer: any; onSaved: () => void };

// --- SVG Icons with proper <path> so no (?) question mark appears ---
const SvgIcon = ({ d, ...rest }: { d: string } & IconProps) => (
  <Icon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d={d} />
  </Icon>
);

const ICONS = {
  building: "M3 21h18M3 7v14M21 7v14M6 11h2M6 15h2M11 11h2M11 15h2M16 11h2M16 15h2M9 3h6v4H9z",
  pin: "M12 2a8 8 0 0 0-8 8c0 5.25 8 12 8 12s8-6.75 8-12a8 8 0 0 0-8-8zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6z",
  fileText: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M8 13h8 M8 17h8",
  globe: "M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 0c2.5 3 4 6.5 4 10s-1.5 7-4 10m0-20c-2.5 3-4 6.5-4 10s1.5 7 4 10M2 12h20",
  phone: "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z",
  mail: "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6",
  check: "M20 6L9 17l-5-5",
  infoCircle: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 16v-4 M12 8h.01",
};

const inputProps = {
  size: "sm",
  bg: "white",
  borderColor: "#d0d5dd",
  borderRadius: "md",
  _focus: { borderColor: "blue.500", boxShadow: "0 0 0 1px #2563eb" },
} as const;

const Req = () => (
  <Text as="span" color="red.500">
    {" "}
    *
  </Text>
);

const Label = ({ children, required }: { children: React.ReactNode; required?: boolean }) => (
  <FormLabel fontSize="xs" fontWeight="medium" color="gray.700" mb={1}>
    {children}
    {required && <Req />}
  </FormLabel>
);

// Fallback Industries list
const DEFAULT_INDUSTRIES = ["Digital Marketing", "E-commerce", "IT Services & Software", "Manufacturing", "Healthcare & Pharmaceuticals", "Retail & Consumer Goods", "Financial Services & Banking", "Real Estate & Construction", "Education & EdTech", "Media & Entertainment", "Logistics & Supply Chain", "Other"];

// Fallback Countries
const DEFAULT_COUNTRIES = ["United States", "India", "United Kingdom", "Australia", "Canada", "United Arab Emirates", "Germany", "France", "Singapore", "Other"];

// Common Tax ID formats by country configuration
export const TAX_CONFIGS: Record<
  string,
  {
    types: string[];
    defaultType: string;
    label: string;
    placeholder: string;
    formatHint: string;
    regex: RegExp;
    sample: string;
  }
> = {
  "United States": {
    types: ["EIN (Employer Identification Number)", "SSN (Social Security Number)", "State Tax ID"],
    defaultType: "EIN (Employer Identification Number)",
    label: "EIN Number",
    placeholder: "12-3456789",
    formatHint: "XX-XXXXXXX (e.g. 12-3456789)",
    regex: /^\d{2}-?\d{7}$/,
    sample: "12-3456789",
  },
  India: {
    types: ["GSTIN (Goods and Services Tax Identification Number)", "PAN (Permanent Account Number)", "TAN"],
    defaultType: "GSTIN (Goods and Services Tax Identification Number)",
    label: "GSTIN Number",
    placeholder: "27ABCDE1234F1Z5",
    formatHint: "15-digit alphanumeric (e.g. 27ABCDE1234F1Z5)",
    regex: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
    sample: "27ABCDE1234F1Z5",
  },
  "United Kingdom": {
    types: ["VAT Registration Number", "Company Registration Number (CRN)"],
    defaultType: "VAT Registration Number",
    label: "VAT Number",
    placeholder: "GB123456789",
    formatHint: "GB + 9 digits (e.g. GB123456789)",
    regex: /^GB\s?[0-9]{9}$/i,
    sample: "GB123456789",
  },
  Australia: {
    types: ["ABN (Australian Business Number)", "ACN (Australian Company Number)"],
    defaultType: "ABN (Australian Business Number)",
    label: "ABN Number",
    placeholder: "12 345 678 901",
    formatHint: "11 digits (e.g. 12 345 678 901)",
    regex: /^(\d{2}\s?\d{3}\s?\d{3}\s?\d{3}|\d{11})$/,
    sample: "12 345 678 901",
  },
  Canada: {
    types: ["Business Number (BN)", "GST/HST Number", "QST (Quebec Sales Tax)"],
    defaultType: "Business Number (BN)",
    label: "BN Number",
    placeholder: "123456789RT0001",
    formatHint: "9 digits + RT0001 (e.g. 123456789RT0001)",
    regex: /^\d{9}(RT\d{4})?$/i,
    sample: "123456789RT0001",
  },
  "United Arab Emirates": {
    types: ["TRN (Tax Registration Number)"],
    defaultType: "TRN (Tax Registration Number)",
    label: "TRN Number",
    placeholder: "100123456700003",
    formatHint: "15 digits (e.g. 100123456700003)",
    regex: /^\d{15}$/,
    sample: "100123456700003",
  },
  UAE: {
    types: ["TRN (Tax Registration Number)"],
    defaultType: "TRN (Tax Registration Number)",
    label: "TRN Number",
    placeholder: "100123456700003",
    formatHint: "15 digits (e.g. 100123456700003)",
    regex: /^\d{15}$/,
    sample: "100123456700003",
  },
};

const DEFAULT_TAX_CONFIG = {
  types: ["Tax ID / VAT Registration Number", "National Business Number"],
  defaultType: "Tax ID / VAT Registration Number",
  label: "Tax ID Number",
  placeholder: "Enter Tax ID",
  formatHint: "Valid local tax identifier",
  regex: /^[A-Za-z0-9\-\s]{4,20}$/,
  sample: "TAX12345678",
};

const buildRegexFromMask = (mask?: string | null): RegExp | null => {
  if (!mask) return null;

  const parts = mask
    .split("+")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return null;

  let pattern = "";

  for (const part of parts) {
    let m: RegExpMatchArray | null;

    if ((m = part.match(/^(\d+) digits?$/i))) {
      pattern += `\\d{${m[1]}}`;
    } else if ((m = part.match(/^(\d+) check digit$/i))) {
      pattern += `\\d{${m[1]}}`;
    } else if ((m = part.match(/^(\d+) or (\d+) characters?$/i))) {
      pattern += `[A-Za-z0-9]{${m[1]},${m[2]}}`;
    } else if ((m = part.match(/^(\d+) characters?$/i))) {
      pattern += `[A-Za-z0-9]{${m[1]}}`;
    } else if ((m = part.match(/^(\d+) letters?$/i))) {
      pattern += `[A-Za-z]{${m[1]}}`;
    } else if (/^letter$/i.test(part)) {
      pattern += `[A-Za-z]`;
    } else if (/^[A-Z]{1,4}$/.test(part)) {
      pattern += part; // fixed prefix such as GB, DE, CHE
    } else if (/[()/]/.test(part)) {
      // optional suffix like "MVA (VAT)" or "MWST/TVA/IVA"
      const words = part
        .replace(/\(.*?\)/g, "")
        .split("/")
        .map((w) => w.trim())
        .filter(Boolean);
      if (words.length === 0 || words.some((w) => !/^[A-Z]+$/.test(w))) return null;
      pattern += `(?:${words.join("|")})?`;
    } else {
      return null;
    }
  }

  return new RegExp(`^${pattern}$`, "i");
};

/* ------------------------------------------------------------------ */
/* Main Client Profile View with 3 Clean Tabs                          */
/* ------------------------------------------------------------------ */
export default function ClientProfileView({ customer, onSaved }: Props) {
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);

  // 0: Basic Information, 1: Billing Address, 2: GST & Tax Details
  const [activeTab, setActiveTab] = useState<number>(0);
  const [saving, setSaving] = useState(false);

  // Dropdown master lists
  const [industryOptions, setIndustryOptions] = useState<string[]>(DEFAULT_INDUSTRIES);
  const [countryOptions, setCountryOptions] = useState<string[]>(DEFAULT_COUNTRIES);

  const [countryData, setCountryData] = useState<any[]>([]);
  const [stateData, setStateData] = useState<any[]>([]);
  const [cityData, setCityData] = useState<any[]>([]);
  const hasSavedTaxId = (c: any) => c.taxDetails?.isTaxRegistered !== undefined && c.taxDetails?.isTaxRegistered !== "yes" ? false : Boolean(c.gstin || c.taxDetails?.taxIdNumber);
  const [validated, setValidated] = useState<boolean | null>(() => hasSavedTaxId(customer));

  // Load Industry Type from Master API: Dropdown/IndustryType
  useEffect(() => {
    const loadIndustries = async () => {
      try {
        const res = await fetchApi<any>("Dropdown/IndustryType", "GET");
        if (Array.isArray(res) && res.length > 0) {
          const names = res.map((item: any) => (typeof item === "string" ? item : item.industryName || item.name || item.industryTypeName || item.industryTitle)).filter(Boolean);
          if (names.length) {
            setIndustryOptions(Array.from(new Set([...names, ...DEFAULT_INDUSTRIES])));
          }
        }
      } catch (err) {
        console.error("Could not load industry types from master API:", err);
      }
    };

    const loadCountries = async () => {
      try {
        const res = await fetchApi<any>("Dropdown/country-details", "GET");

        if (Array.isArray(res) && res.length > 0) {
          // Keep country names for the existing Tax & GST fields.
          const names = res.map((item: any) => (typeof item === "string" ? item : item.countryName || item.name || item.country)).filter(Boolean);
          setCountryData(Array.isArray(res) ? res : []);
          console.log("country sample:", res[0]);
          if (names.length) {
            setCountryOptions(Array.from(new Set([...DEFAULT_COUNTRIES, ...names])));
          }
        }
      } catch (err) {
        console.error("Could not load country list:", err);
      }
    };

    loadIndustries();
    loadCountries();
  }, []);

  // Form State initialized from customer record
  const initialForm = () => {
    // Extract website from customer or company
    const website = customer.website ?? customer.websiteUrl ?? customer.companyWebsite ?? customer.company?.websiteUrl ?? customer.company?.website ?? "";

    // Extract industry
    const industry = customer.industry ?? customer.industryName ?? customer.company?.industryTypeName ?? customer.company?.industryName ?? "Digital Marketing";

    // Extract billing address & tax info
    const b = customer.billingAddress ?? {};
    const profile = customer.billingProfile ?? {};
    const tax = customer.taxDetails ?? {};

    const initialCountry = b.country || tax.country || customer.country || customer.company?.country?.name || "United States";

    const taxCountry = tax.country || initialCountry;
    const cfg = TAX_CONFIGS[taxCountry] || DEFAULT_TAX_CONFIG;

    return {
      // 1. Basic Information
      name: customer.name ?? customer.customer_Name ?? "",
      industry: industry,
      website: website,
      phone: customer.phone ?? customer.poc?.phoneNumber ?? customer.company?.phone ?? "",
      email: customer.email ?? customer.poc?.email ?? customer.company?.companyEmail ?? "",
      currency: customer.currency ?? profile.currency ?? "USD",
      status: customer.status !== false ? "Active" : "Inactive",
      description: customer.description ?? "",
      sendInvoiceEmail: customer.sendInvoiceEmail ?? true,

      // 2. Billing Address
      sameAsRegistered: b.sameAsRegistered ?? false,
      line1: b.line1 ?? customer.addressLine1 ?? "",
      line2: b.line2 ?? "",
      city: b.city ?? customer.city ?? customer.company?.city?.name ?? "",
      state: b.state ?? customer.state ?? customer.company?.state?.name ?? "",
      postalCode: b.postalCode ?? b.pinCode ?? customer.postalZipCode ?? customer.company?.postalZipCode ?? "",
      billingCountry: initialCountry,
      placeOfSupply: b.placeOfSupply ?? initialCountry,

      // 3. Tax & GST Details
      taxCountry: taxCountry,
      isTaxRegistered: tax.isTaxRegistered ?? "yes",
      taxIdType: tax.taxIdType ?? (taxCountry === "India" ? "GSTIN (Goods and Services Tax Identification Number)" : cfg.defaultType),
      taxIdNumber: tax.taxIdNumber ?? customer.gstin ?? profile.gstin ?? "",
      legalName: tax.legalName ?? profile.billingName ?? customer.name ?? customer.customer_Name ?? "",
    };
  };

  const [f, setF] = useState(initialForm);
  const [initialSnapshot, setInitialSnapshot] = useState(() => JSON.stringify(initialForm()));
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const selectedCountry = countryData.find((c) => c.countryName === f.billingCountry);

    if (!selectedCountry) {
      setStateData([]);
      setCityData([]);
      return;
    }

    const loadStates = async () => {
      try {
        const res = await fetchApi<any>(`Dropdown/state/${selectedCountry.countryId}`, "GET");
        setStateData(Array.isArray(res) ? res : []);
      } catch (err) {
        console.error("Could not load states:", err);
      }
    };

    loadStates();
  }, [countryData, f.billingCountry]);

  useEffect(() => {
    const selectedState = stateData.find((s) => s.stateName === f.state);

    if (!selectedState) {
      setCityData([]);
      return;
    }

    const loadCities = async () => {
      try {
        const res = await fetchApi<any>(`Dropdown/city/${selectedState.stateId}`, "GET");
        setCityData(Array.isArray(res) ? res : []);
      } catch (err) {
        console.error("Could not load cities:", err);
      }
    };

    loadCities();
  }, [stateData, f.state]);

  useEffect(() => {
    const fresh = initialForm();
    setF(fresh);
    setInitialSnapshot(JSON.stringify(fresh));
    setValidated(hasSavedTaxId(customer));
  }, [customer]);

  const findCountry = (name: string) => countryData.find((c) => (c.countryName || "").trim().toLowerCase() === (name || "").trim().toLowerCase());

  const set = (k: string, v: any) => {
    setF((p) => {
      const updated = { ...p, [k]: v };

      if (k === "billingCountry" && (!p.placeOfSupply || p.placeOfSupply === p.billingCountry)) {
        updated.placeOfSupply = v;
      }
      if (k === "taxCountry") {
        const c = findCountry(v);
        console.log("tax country picked:", v, "found:", c);
        updated.taxIdType = c?.taxIdType || (TAX_CONFIGS[v] || DEFAULT_TAX_CONFIG).defaultType;
        updated.currency = c?.currency || p.currency;
        setValidated(null);
      }
      return updated;
    });
  };

  // Country changed -> clear state & city
  const handleCountryChange = (value: string) => {
    setF((p) => ({
      ...p,
      billingCountry: value,
      state: "",
      city: "",
      placeOfSupply: !p.placeOfSupply || p.placeOfSupply === p.billingCountry ? value : p.placeOfSupply,
      currency: findCountry(value)?.currency || p.currency,
    }));
    setStateData([]);
    setCityData([]);
  };

  // State changed -> clear city
  const handleStateChange = (value: string) => {
    setF((p) => ({ ...p, state: value, city: "" }));
    setCityData([]);
  };

  // Sync with registered address
  const handleSameAsRegistered = (checked: boolean) => {
    setF((p) => ({
      ...p,
      sameAsRegistered: checked,
      ...(checked
        ? {
          line1: customer.addressLine1 || p.line1,
          city: customer.company?.city?.name || p.city,
          state: customer.company?.state?.name || p.state,
          postalCode: customer.company?.postalZipCode || p.postalCode,
          billingCountry: customer.company?.country?.name || p.billingCountry,
          placeOfSupply: customer.company?.country?.name || p.placeOfSupply,
          currency: countryData.find((c) => c.countryName === customer.company?.country?.name)?.currency || p.currency,
        }
        : {}),
    }));
  };
  const billingCountryList = countryData.length > 0 ? countryData.map((c) => c.countryName) : countryOptions;
  const currencyOptions = useMemo(() => {
    const map = new Map<string, string>();
    countryData.forEach((c) => {
      if (c.currency) map.set(c.currency, c.currencySymbol || "");
    });
    if (f.currency && !map.has(f.currency)) map.set(f.currency, "");
    return Array.from(map, ([code, symbol]) => ({ code, symbol })).sort((a, b) => a.code.localeCompare(b.code));
  }, [countryData, f.currency]);
  const isDirty = useMemo(() => JSON.stringify(f) !== initialSnapshot, [f, initialSnapshot]);

  const currentTaxConfig = useMemo(() => {
    const base = TAX_CONFIGS[f.taxCountry] || DEFAULT_TAX_CONFIG;
    const c = countryData.find((x) => x.countryName === f.taxCountry);
    if (!c) return base;

    const maskRegex = buildRegexFromMask(c.taxIdTypeMask);
    const strict = TAX_CONFIGS[f.taxCountry];
    const sameType = !!strict && (f.taxIdType || "").split(" ")[0].toUpperCase() === strict.defaultType.split(" ")[0].toUpperCase();

    return {
      ...base,
      types: Array.from(new Set([c.taxIdType, f.taxIdType].filter(Boolean))),
      defaultType: c.taxIdType || base.defaultType,
      label: c.taxIdType ? `${c.taxIdType} Number` : base.label,
      placeholder: c.taxIdTypeMask || base.placeholder,
      formatHint: c.taxIdTypeMask || base.formatHint,
      regex: (sameType ? strict!.regex : maskRegex) ?? DEFAULT_TAX_CONFIG.regex,
    };
  }, [f.taxCountry, f.taxIdType, countryData]);

  // Single source of truth for tax ID validation (used by Validate button AND Save)
  const getTaxIdError = (raw: string): string => {
    const val = (raw || "").trim();
    if (!val) return `${currentTaxConfig.label} is required`;
    const compact = val.replace(/[\s-]/g, "");
    const ok = currentTaxConfig.regex.test(val) || currentTaxConfig.regex.test(compact);
    return ok ? "" : `Invalid format. Expected: ${currentTaxConfig.formatHint}`;
  };

  // Tax ID validate button
  const handleValidateTaxId = () => {
    const err = getTaxIdError(f.taxIdNumber);
    setValidated(!err);
    setErrors((prev) => {
      const copy = { ...prev };
      if (err) copy.taxIdNumber = err;
      else delete copy.taxIdNumber;
      return copy;
    });
  };

  // Save handler
  const handleSubmit = async () => {
    const e: Record<string, string> = {};

    // Validate active tab or all tabs
    if (!f.name.trim()) e.name = "Client name is required";
    if (!f.currency) e.currency = "Currency is required";
    if (f.email && !/^\S+@\S+\.\S+$/.test(f.email)) e.email = "Enter a valid email";

    if (activeTab === 1) {
      if (!f.line1.trim()) e.line1 = "Address Line 1 is required";
      if (stateData.length > 0 && !f.state.trim()) e.state = "State is required";
      if (cityData.length > 0 && !f.city.trim()) e.city = "City is required";

      const postal = f.postalCode.trim();
      if (!postal) e.postalCode = "Postal / ZIP Code is required";
      else if (postal.length < 3 || postal.length > 12) e.postalCode = "Postal / ZIP Code must be between 3 and 12 characters";
      if (!f.billingCountry) e.billingCountry = "Country is required";
    }

    if (activeTab === 2) {
      if (!f.taxCountry) e.taxCountry = "Tax Country is required";
      if (f.isTaxRegistered === "yes") {
        const taxErr = getTaxIdError(f.taxIdNumber);
        if (taxErr) e.taxIdNumber = taxErr;
      }
    }

    setErrors(e);
    if (Object.keys(e).length > 0) {
      addToast({ message: "Please check the required fields.", status: "error" });
      return;
    }

    setSaving(true);
    try {
      const isRegistered = f.isTaxRegistered === "yes";
      const payload = {
        customerId: customer.id,
        // Basic Info
        name: f.name.trim(),
        industry: f.industry,
        website: f.website.trim(),
        phone: f.phone.trim(),
        email: f.email.trim(),
        currency: f.currency,
        status: f.status === "Active",
        description: f.description.trim(),
        sendInvoiceEmail: f.sendInvoiceEmail,
        // Billing Address
        sameAsRegistered: f.sameAsRegistered,
        billingAddressLine1: f.line1.trim(),
        billingAddressLine2: f.line2.trim(),
        billingCity: f.city.trim(),
        billingState: f.state.trim(),
        billingPostalCode: f.postalCode.trim(),
        billingCountry: f.billingCountry,
        placeOfSupply: f.placeOfSupply,

        // Tax Details
        taxCountry: f.taxCountry,
        isTaxRegistered: f.isTaxRegistered,
        taxIdType: isRegistered ? f.taxIdType : "",
        taxIdNumber: isRegistered ? f.taxIdNumber.trim() : "",
        legalName: (f.legalName || f.name).trim(),
        gstin: isRegistered && f.taxCountry === "India" ? f.taxIdNumber.trim().toUpperCase() : "",
      };

      const res = await fetchApi<any>(`customers/${customer.id}/profile`, "PUT", payload, null, "");

      if (res !== false) {
        addToast({
          message: "Customer profile updated successfully.",
          status: "success",
        });
        onSaved();
      }
    } catch (err: any) {
      try {
        await Promise.all([
          fetchApi( `customers/${customer.id}/basic-info`, "PUT",
            {
              name: f.name,
              industry: f.industry,
              website: f.website,
              phone: f.phone,
              email: f.email,
              currency: f.currency,
              status: f.status === "Active",
              description: f.description,
              sendInvoiceEmail: f.sendInvoiceEmail,
            },
            null,
            "",
          ),
          fetchApi( `customers/${customer.id}/billing-address`, "PUT",
            {
              sameAsRegistered: f.sameAsRegistered,
              line1: f.line1,
              line2: f.line2,
              city: f.city,
              state: f.state,
              postalCode: f.postalCode,
              country: f.billingCountry,
              placeOfSupply: f.placeOfSupply,
            },
            null,
            "",
          ),
          fetchApi( `customers/${customer.id}/tax-details`, "PUT",
            {
              country: f.taxCountry,
              isTaxRegistered: f.isTaxRegistered,
              taxIdType: f.isTaxRegistered === "yes" ? f.taxIdType : "",
              taxIdNumber: f.isTaxRegistered === "yes" ? f.taxIdNumber : "",
              legalName: f.legalName,
              gstin: f.isTaxRegistered === "yes" && f.taxCountry === "India" ? f.taxIdNumber : "",
            },
            null,
            "",
          ),
        ]);
        addToast({ message: "Customer profile updated successfully.", status: "success" });
        onSaved();
      } catch {
        addToast({ message: "Could not save profile details.", status: "error" });
      }
    } finally {
      setSaving(false);
    }
  };

  // ONLY 3 tabs as requested!
  const tabsNav = [
    { label: "Basic Information", icon: ICONS.building },
    { label: "Billing Address", icon: ICONS.pin },
    { label: "Tax & GST Details", icon: ICONS.fileText },
  ];

  const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  return (
    <Box>
      {/* Sub-navigation tabs: ONLY 3 tabs as requested */}
      <HStack spacing={8} borderBottom="1px solid" borderColor="gray.200" mb={6} pb={0} overflowX="auto">
        {tabsNav.map((t, idx) => {
          const isSelected = activeTab === idx;
          return (
            <HStack key={t.label} as="button" spacing={2} py={3} px={2} borderBottom="2px solid" borderColor={isSelected ? "blue.600" : "transparent"} color={isSelected ? "blue.600" : "gray.600"} fontWeight={isSelected ? "600" : "500"} fontSize="sm" cursor="pointer" _hover={{ color: "blue.600" }} onClick={() => setActiveTab(idx)} whiteSpace="nowrap" transition="all 0.15s ease">
              <SvgIcon d={t.icon} boxSize={4} color={isSelected ? "blue.600" : "gray.500"} />
              <Text m={0}>{t.label}</Text>
            </HStack>
          );
        })}
      </HStack>

      {/* ======================================================== */}
      {/* TAB 0: Basic Information                                  */}
      {/* ======================================================== */}
      {activeTab === 0 && (
        <Box maxW="850px">
          <Box {...cardProps} p={6} mb={5}>
            <HStack spacing={2} mb={5} color="blue.700">
              <SvgIcon d={ICONS.building} boxSize={5} />
              <Heading size="sm">Basic Information</Heading>
            </HStack>

            <VStack align="stretch" spacing={4}>
              {/* Client Name */}
              <FormControl isInvalid={!!errors.name}>
                <Label required>Client Name</Label>
                <Input {...inputProps} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Devansh Solutions PVT" />
                <FormErrorMessage>{errors.name}</FormErrorMessage>
              </FormControl>

              {/* Industry from master (Client Type is removed) */}
              <FormControl>
                <Label>Industry</Label>
                <Select {...inputProps} value={f.industry} onChange={(e) => set("industry", e.target.value)} placeholder="Select Industry">
                  {industryOptions.map((ind) => (
                    <option key={ind} value={ind}>
                      {ind}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {/* Website prefilled from customer with Globe Icon */}
              <FormControl>
                <Label>Website</Label>
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none" color="gray.500">
                    <SvgIcon d={ICONS.globe} boxSize={4} />
                  </InputLeftElement>
                  <Input {...inputProps} pl={9} value={f.website} onChange={(e) => set("website", e.target.value)} placeholder="https://www.abc.com" />
                </InputGroup>
              </FormControl>

              {/* Phone & Email side by side with Icons */}
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                <FormControl>
                  <Label>Phone</Label>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none" color="gray.500">
                      <SvgIcon d={ICONS.phone} boxSize={3.5} />
                    </InputLeftElement>
                    <Input {...inputProps} pl={9} value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 415 555 1234" />
                  </InputGroup>
                </FormControl>

                <FormControl isInvalid={!!errors.email}>
                  <Label>Email</Label>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none" color="gray.500">
                      <SvgIcon d={ICONS.mail} boxSize={3.5} />
                    </InputLeftElement>
                    <Input {...inputProps} pl={9} type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="billing@abc.com" />
                  </InputGroup>
                  <FormErrorMessage>{errors.email}</FormErrorMessage>
                </FormControl>
              </SimpleGrid>

              {/*Status */}
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                <FormControl>
                  <Label>Status</Label>
                  <Select {...inputProps} value={f.status} onChange={(e) => set("status", e.target.value)}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Select>
                </FormControl>
              </SimpleGrid>

              {/* Client Description */}
              <FormControl>
                <Label>Client Description</Label>
                <Textarea {...inputProps} rows={3} value={stripHtml(f.description || "")} onChange={(e) => set("description", e.target.value)} placeholder="Add notes about client..." />
              </FormControl>

              {/* Send invoices by email */}
              {/* <Box pt={1}>
                <Checkbox size="sm" isChecked={f.sendInvoiceEmail} onChange={(e) => set("sendInvoiceEmail", e.target.checked)} colorScheme="blue">
                  <Text fontSize="xs" fontWeight="medium" color="gray.700" m={0}>
                    Send invoices and communication to client by email
                  </Text>
                </Checkbox>
                <Text fontSize="11px" color="gray.500" ml={6} mt={0.5}>
                  Invoice, payment reminders and important notifications will be sent to the client's billing email.
                </Text>
              </Box> */}
            </VStack>
          </Box>

          <Flex justify="flex-end" gap={3}>
            <Button size="sm" variant="outline" bg="white" borderColor="#d0d5dd" onClick={() => {
                setF(initialForm());
                setErrors({});
              }}
              isDisabled={saving}
            >
              Cancel
            </Button>
            <Button size="sm" colorScheme="blue" onClick={handleSubmit} isLoading={saving} isDisabled={!isDirty} px={6}>
              Save Basic Information
            </Button>
          </Flex>
        </Box>
      )}

      {/* ======================================================== */}
      {/* TAB 1: Billing Address                                    */}
      {/* ======================================================== */}
      {activeTab === 1 && (
        <Box maxW="850px">
          <Box {...cardProps} p={6} mb={5}>
            <HStack spacing={2} mb={5} color="blue.700">
              <SvgIcon d={ICONS.pin} boxSize={5} />
              <Heading size="sm">Billing Address</Heading>
            </HStack>

            <VStack align="stretch" spacing={4}>
              {/* Same as Registered Address */}
              <Checkbox size="sm" isChecked={f.sameAsRegistered} onChange={(e) => handleSameAsRegistered(e.target.checked)} colorScheme="blue">
                <Text fontSize="xs" fontWeight="medium" color="gray.700" m={0}>
                  Same as Registered Address
                </Text>
              </Checkbox>

              {/* Address Line 1 */}
              <FormControl isInvalid={!!errors.line1}>
                <Label required>Address Line 1</Label>
                <Input {...inputProps} value={f.line1} onChange={(e) => set("line1", e.target.value)} placeholder="123 Business Park" />
                <FormErrorMessage>{errors.line1}</FormErrorMessage>
              </FormControl>

              {/* Address Line 2 */}
              <FormControl>
                <Label>Address Line 2</Label>
                <Input {...inputProps} value={f.line2} onChange={(e) => set("line2", e.target.value)} placeholder="Suite 501, Tower A" />
              </FormControl>

              {/* City & State / Province / Region */}
              {/* <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                <FormControl isInvalid={!!errors.city}>
                  <Label required>City</Label>
                  <Select {...inputProps} value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Select City">
                    {cityData.map((c) => (
                      <option key={c.cityId} value={c.cityName}>
                        {c.cityName}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.city}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <Label>State / Province / Region</Label>
                  <Select
                    {...inputProps}
                    value={f.state}
                    onChange={(e) => {
                      setF((p) => ({
                        ...p,
                        state: e.target.value,
                        city: "",
                      }));
                      setCityData([]);
                    }}
                    placeholder="Select State"
                  >
                    {stateData.map((s) => (
                      <option key={s.stateId} value={s.stateName}>
                        {s.stateName}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid> */}

              {/* 1. COUNTRY */}
              <FormControl isInvalid={!!errors.billingCountry}>
                <Label required>Country</Label>
                <Select {...inputProps} value={f.billingCountry} onChange={(e) => handleCountryChange(e.target.value)} placeholder="Select Country">
                  {billingCountryList.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.billingCountry}</FormErrorMessage>
              </FormControl>

              {/* 2. STATE  &  3. CITY */}
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                <FormControl isInvalid={!!errors.state}>
                  <Label>State / Province / Region</Label>
                  <Select {...inputProps} value={f.state} onChange={(e) => handleStateChange(e.target.value)} placeholder="Select State" isDisabled={!f.billingCountry || stateData.length === 0}>
                    {stateData.map((s) => (
                      <option key={s.stateId} value={s.stateName}>
                        {s.stateName}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.state}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.city}>
                  <Label required>City</Label>
                  <Select {...inputProps} value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Select City" isDisabled={!f.state || cityData.length === 0}>
                    {cityData.map((c) => (
                      <option key={c.cityId} value={c.cityName}>
                        {c.cityName}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.city}</FormErrorMessage>
                </FormControl>
              </SimpleGrid>

              {/* Postal / ZIP Code & Country */}
              <FormControl isInvalid={!!errors.postalCode}>
                <Label required>Postal / ZIP Code</Label>
                <Input {...inputProps} maxLength={12} value={f.postalCode} onChange={(e) => set("postalCode", e.target.value)} placeholder="10001" />
                <FormErrorMessage>{errors.postalCode}</FormErrorMessage>
              </FormControl>

              {/* Blue Callout: Place of Supply (For Tax Purposes) */}
              <Box bg="blue.50" border="1px solid" borderColor="blue.100" borderRadius="md" p={3.5} mt={2}>
                <HStack spacing={2} mb={1}>
                  <SvgIcon d={ICONS.pin} boxSize={3.5} color="blue.600" />
                  <Text fontSize="xs" fontWeight="semibold" color="blue.800" m={0}>
                    Place of Supply (For Tax Purposes)
                  </Text>
                </HStack>
                <Text fontSize="11px" color="blue.600" mb={2}>
                  Used to determine applicable tax for invoicing.
                </Text>

                <Select {...inputProps} bg="white" borderColor="blue.200" value={f.placeOfSupply} onChange={(e) => set("placeOfSupply", e.target.value)}>
                  {countryOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
                <Text fontSize="11px" color="gray.500" mt={1.5}>
                  Usually same as the billing address country.
                </Text>
              </Box>
            </VStack>
          </Box>

          <Flex justify="flex-end" gap={3}>
            <Button size="sm" variant="outline" bg="white" borderColor="#d0d5dd" onClick={() => {
                setF(initialForm());
                setErrors({});
              }}
              isDisabled={saving}
            >
              Cancel
            </Button>
            <Button size="sm" colorScheme="blue" onClick={handleSubmit} isLoading={saving} isDisabled={!isDirty} px={6}>
              Save Billing Address
            </Button>
          </Flex>
        </Box>
      )}

      {/* ======================================================== */}
      {/* TAB 2: Tax & GST Details                                  */}
      {/* ======================================================== */}
      {activeTab === 2 && (
        <Box maxW="900px">
          <Box {...cardProps} p={6} mb={5}>
            <HStack spacing={2} mb={5} color="blue.700">
              <SvgIcon d={ICONS.fileText} boxSize={5} />
              <Heading size="sm">Tax & GST Details</Heading>
            </HStack>

            <VStack align="stretch" spacing={4}>
              {/* Country Selector */}
              <FormControl isInvalid={!!errors.taxCountry}>
                <Label required>Country</Label>
                <Select {...inputProps} value={f.taxCountry} onChange={(e) => set("taxCountry", e.target.value)} borderColor="blue.400" fontWeight="500">
                  {countryOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.taxCountry}</FormErrorMessage>
              </FormControl>
              {/* Currency (read-only) */}
              <FormControl isInvalid={!!errors.currency}>
                <Label required>Currency</Label>
                <Select {...inputProps} value={f.currency} isDisabled _disabled={{ opacity: 1, bg: "gray.50", cursor: "not-allowed" }}>
                  {currencyOptions.length === 0 && <option value={f.currency}>{f.currency}</option>}
                  {currencyOptions.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code}
                      {c.symbol ? ` (${c.symbol})` : ""}
                    </option>
                  ))}
                </Select>
                <Text fontSize="11px" color="gray.500" mt={1}>
                  Set automatically from the billing country.
                </Text>
                <FormErrorMessage>{errors.currency}</FormErrorMessage>
              </FormControl>

              {/* Info Message */}
              <HStack bg="blue.50" p={2.5} borderRadius="md" spacing={2} align="center">
                <SvgIcon d={ICONS.infoCircle} boxSize={4} color="blue.600" flexShrink={0} />
                <Text fontSize="xs" color="blue.700" m={0}>
                  Tax registration fields are based on the selected country.
                </Text>
              </HStack>

              {/* Is the client registered for tax? */}
              <FormControl>
                <Label required>Is the client registered for tax?</Label>
                <RadioGroup
                  value={f.isTaxRegistered}
                  onChange={(v) => {
                    set("isTaxRegistered", v);
                    if (v !== "yes") {
                      setF((p) => ({ ...p, isTaxRegistered: v, taxIdNumber: "", taxIdType: "" }));
                      setValidated(null);
                      setErrors((prev) => {
                        const copy = { ...prev };
                        delete copy.taxIdNumber;
                        return copy;
                      });
                    }
                  }}
                >
                  <HStack spacing={6} mt={1}>
                    <Radio size="sm" value="yes" colorScheme="blue">
                      <Text fontSize="xs">Yes</Text>
                    </Radio>
                    <Radio size="sm" value="no" colorScheme="blue">
                      <Text fontSize="xs">No</Text>
                    </Radio>
                    <Radio size="sm" value="not_sure" colorScheme="blue">
                      <Text fontSize="xs">Not Sure</Text>
                    </Radio>
                  </HStack>
                </RadioGroup>
              </FormControl>

              {f.isTaxRegistered === "yes" && (
                <>
                  {/* Tax ID Type */}
                  <FormControl>
                    <Label required>Tax ID Type</Label>
                    <Select
                      {...inputProps}
                      value={f.taxIdType}
                      onChange={(e) => {
                        set("taxIdType", e.target.value);
                        setValidated(null);
                      }}
                      borderColor="blue.400"
                    >
                      {currentTaxConfig.types.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Tax ID Number with Validate Button */}
                  <FormControl isInvalid={!!errors.taxIdNumber}>
                    <Label required>{currentTaxConfig.label}</Label>
                    <HStack>
                      <Input
                        {...inputProps}
                        value={f.taxIdNumber}
                        onChange={(e) => {
                          set("taxIdNumber", e.target.value);
                          setValidated(null);
                        }}
                        placeholder={currentTaxConfig.placeholder}
                      />
                      <Button size="sm" variant="outline" colorScheme="blue" px={4} onClick={handleValidateTaxId}>
                        Validate
                      </Button>
                    </HStack>
                    <FormErrorMessage>{errors.taxIdNumber}</FormErrorMessage>
                  </FormControl>

                  {/* Validated Format Banner */}
                  {validated && (
                    <Box bg="#F0FDF4" border="1px solid #BBF7D0" borderRadius="md" p={3.5}>
                      <HStack spacing={2} mb={1}>
                        <SvgIcon d={ICONS.check} boxSize={4} color="green.600" />
                        <Text fontSize="xs" fontWeight="semibold" color="green.800" m={0}>
                          Valid {f.taxIdType.split(" ")[0]} Format
                        </Text>
                      </HStack>
                      <Text fontSize="11px" color="gray.700" m={0}>
                        Business Name: <b>{f.legalName || f.name || "ABC Pvt Ltd"}</b>
                      </Text>
                      <Text fontSize="11px" color="gray.700" m={0}>
                        Country: <b>{f.taxCountry}</b>
                      </Text>
                      <Text fontSize="11px" color="gray.500" mt={1}>
                        Format: {currentTaxConfig.formatHint}
                      </Text>
                    </Box>
                  )}

                  {/* Legal Business Name */}
                  <FormControl>
                    <Label>Legal Business Name (as per Tax Registration)</Label>
                    <Input {...inputProps} value={f.legalName} onChange={(e) => set("legalName", e.target.value)} placeholder={f.name || "Devansh Solutions PVT"} />
                  </FormControl>

                  {/* Info banner at bottom */}
                  <HStack bg="blue.50" p={2.5} borderRadius="md" spacing={2} align="start">
                    <SvgIcon d={ICONS.infoCircle} boxSize={4} color="blue.600" flexShrink={0} mt={0.5} />
                    <Text fontSize="11px" color="blue.700" m={0} lineHeight="1.4">
                      For non-Indian clients, capture the applicable tax registration number such as EIN (US), VAT (UK), ABN (Australia) or other local tax ID.
                    </Text>
                  </HStack>
                </>
              )}
            </VStack>
          </Box>

          {/* Common Tax ID Types by Country table */}
          <Box {...cardProps} p={5} mb={5} bg="white">
            <HStack spacing={2} mb={3} color="blue.700">
              <SvgIcon d={ICONS.globe} boxSize={4.5} />
              <Heading size="xs" textTransform="uppercase" letterSpacing="wider">
                Common Tax ID Types by Country
              </Heading>
            </HStack>

            <SimpleGrid columns={{ base: 2, sm: 3, md: 6 }} spacing={4} pt={2}>
              <Box borderRight={{ base: "none", md: "1px solid" }} borderColor="gray.100" pr={3}>
                <Text fontSize="xs" fontWeight="semibold" color="gray.800" mb={1}>
                  India
                </Text>
                <Text fontSize="xs" color="gray.600">
                  GSTIN
                </Text>
                <Text fontSize="11px" color="gray.400">
                  e.g. 27ABCDE1234F1Z5
                </Text>
              </Box>

              <Box borderRight={{ base: "none", md: "1px solid" }} borderColor="gray.100" pr={3}>
                <Text fontSize="xs" fontWeight="semibold" color="gray.800" mb={1}>
                  United States
                </Text>
                <Text fontSize="xs" color="gray.600">
                  EIN (Employer Identification Number)
                </Text>
                <Text fontSize="11px" color="gray.400">
                  e.g. 12-3456789
                </Text>
              </Box>

              <Box borderRight={{ base: "none", md: "1px solid" }} borderColor="gray.100" pr={3}>
                <Text fontSize="xs" fontWeight="semibold" color="gray.800" mb={1}>
                  United Kingdom
                </Text>
                <Text fontSize="xs" color="gray.600">
                  VAT Registration Number
                </Text>
                <Text fontSize="11px" color="gray.400">
                  e.g. GB123456789
                </Text>
              </Box>

              <Box borderRight={{ base: "none", md: "1px solid" }} borderColor="gray.100" pr={3}>
                <Text fontSize="xs" fontWeight="semibold" color="gray.800" mb={1}>
                  Australia
                </Text>
                <Text fontSize="xs" color="gray.600">
                  ABN (Australian Business Number)
                </Text>
                <Text fontSize="11px" color="gray.400">
                  e.g. 12 345 678 901
                </Text>
              </Box>

              <Box borderRight={{ base: "none", md: "1px solid" }} borderColor="gray.100" pr={3}>
                <Text fontSize="xs" fontWeight="semibold" color="gray.800" mb={1}>
                  Canada
                </Text>
                <Text fontSize="xs" color="gray.600">
                  Business Number (BN)
                </Text>
                <Text fontSize="11px" color="gray.400">
                  e.g. 123456789RT0001
                </Text>
              </Box>

              <Box>
                <Text fontSize="xs" fontWeight="semibold" color="gray.800" mb={1}>
                  UAE
                </Text>
                <Text fontSize="xs" color="gray.600">
                  TRN (Tax Registration Number)
                </Text>
                <Text fontSize="11px" color="gray.400">
                  e.g. 100123456700003
                </Text>
              </Box>
            </SimpleGrid>
          </Box>

          <Flex justify="flex-end" gap={3}>
            <Button size="sm" variant="outline" bg="white" borderColor="#d0d5dd"
              onClick={() => {
                setF(initialForm());
                setErrors({});
                setValidated(hasSavedTaxId(customer));
              }}
              isDisabled={saving}
            >
              Cancel
            </Button>
            <Button size="sm" colorScheme="blue" onClick={handleSubmit} isLoading={saving} isDisabled={!isDirty} px={6}>
              Save Tax & GST Details
            </Button>
          </Flex>
        </Box>
      )}
    </Box>
  );
}

// Backward-compatible individual exports
export function BasicInformationTab({ customer, onSaved }: Props) {
  return <ClientProfileView customer={customer} onSaved={onSaved} />;
}

export function BillingAddressTab({ customer, onSaved }: Props) {
  return <ClientProfileView customer={customer} onSaved={onSaved} />;
}

export function GstTaxTab({ customer, onSaved }: Props) {
  return <ClientProfileView customer={customer} onSaved={onSaved} />;
}
